/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable jsx-a11y/no-autofocus */

import React, { forwardRef, useState } from 'react';
import InputMask from 'react-input-mask'; // Import react-input-mask
import {
    EyeIcon,
    EyeSlashIcon,
    VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { VoiceToText } from 'components/voiceToText';
import { useSpeechToText } from 'hooks/useSpeechToText';

export interface InputProps {
    label?: string;
    placeholder?: string;
    name?: string;
    value?: string | null;
    stt?: boolean;
    onChange?: (value: string) => void;
    error?: string;
    helperText?: string;
    disabled?: boolean;
    required?: boolean;
    autoFocus?: boolean;
    type?: string;
    variant?: 'default' | 'outline';
    textAlign?: 'left' | 'center' | 'right';
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    fullWidth?: boolean;
    size?: 'small' | 'normal'; // Added size prop
    as?: 'input' | 'textarea'; // To support textarea if needed
    rows?: number;
    mask?: string; // Added mask prop
    maskChar?: string; // Optional: Character to use for unfilled parts of the mask
    readOnly?: boolean;
}

export const Input: React.FC<InputProps> = forwardRef<any, InputProps>(
    (
        {
            autoFocus = false,
            label,
            placeholder = '',
            name,
            value,
            onChange,
            error,
            helperText,
            disabled = false,
            required = false,
            type = 'text',
            variant = 'default',
            textAlign = 'left',
            startIcon,
            endIcon,
            fullWidth = false,
            size = 'normal', // Default size
            as = 'input', // Default as 'input'
            rows = 1,
            mask, // Destructure mask
            maskChar = '_', // Default mask character
            readOnly = false,
            stt = false,
        },
        ref
    ) => {
        const [showPassword, setShowPassword] = useState(false);

        const inputType =
            type === 'password' ? (showPassword ? 'text' : 'password') : type;

        const togglePasswordVisibility = () => {
            if (!disabled) {
                setShowPassword((prev) => !prev);
            }
        };

        const {
            mutate,
            isPending: isFetchingText,
            isSuccess,
            reset,
        } = useSpeechToText();
        const shouldShowDefaultEyeIcon = type === 'password' && !endIcon;

        // Define size-based classes
        const sizeClasses = {
            small: `${as === 'input' ? 'h-10' : ''} text-md px-3`, // Example: height 10, small text, padding-x 3
            normal: `${as === 'input' ? 'h-12' : ''} text-base px-4`, // Example: height 12, base text, padding-x 4
        };

        // Common input class names
        const commonInputClasses = `
        w-full
        focus:ring-0
        outline-none
        focus:outline-none
        text-primary-content
        ${
            variant === 'outline'
                ? 'bg-transparent border-b border-border dark:border-border-dark  focus:border-border dark:border-border-dark '
                : 'bg-base-100  input-bordered rounded-2xl'
        }
        ${!disabled ? 'opacity-80' : 'opacity-50 cursor-not-allowed'}
        ${error ? 'input-error border-red-500' : ''}
        ${
            textAlign === 'center'
                ? 'text-center'
                : textAlign === 'right'
                ? 'text-right'
                : 'text-start'
        }
        ${type === 'number' ? 'appearance-none' : ''}
        ${startIcon ? 'ps-10' : ''}
        ${shouldShowDefaultEyeIcon || endIcon ? 'pe-10' : ''}
        ${sizeClasses[size]} /* Apply size-based classes */
    `;

        return (
            <div className="relative">
                {as === 'textarea' && stt && (
                    <div className="absolute end-2 top-2 z-10">
                        <VoiceToText
                            clear={() => {
                                if (onChange) onChange('');
                            }}
                            isFetchingText={isFetchingText}
                            isTextFetched={isSuccess}
                            onAudioUrlChanged={(url, blob) => {
                                if (onChange) {
                                    const file = new File([blob], 'voice', {
                                        type: blob.type,
                                    });
                                    mutate(file, {
                                        onSuccess: (data) => {
                                            onChange(data.data);
                                            setTimeout(() => {
                                                reset();
                                            }, 340);
                                        },
                                    });
                                }
                            }}
                        />
                    </div>
                )}
                <label
                    className={`form-control w-full ${
                        !fullWidth && 'max-w-xs'
                    }`}
                >
                    {/* Label */}
                    {label && (
                        <div className="label flex items-center">
                            <span className="text-md text-primary-content">
                                {label}
                            </span>
                            {required && (
                                <span className="text-red-500 ms-1">*</span>
                            )}
                        </div>
                    )}

                    {/* Input Wrapper */}
                    <div className="relative">
                        {/* Start Icon */}
                        {startIcon && (
                            <span className="absolute start-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                {startIcon}
                            </span>
                        )}

                        {/* Conditional Rendering based on 'as' and 'mask' props */}
                        {as === 'textarea' ? (
                            <textarea
                                ref={ref}
                                name={name}
                                autoFocus={autoFocus}
                                rows={rows}
                                placeholder={placeholder}
                                className={`w-full outline-none resize-none
        focus:outline-none focus:border-primary  focus:ring-0  text-primary-content ${
            stt ? 'pe-10' : ''
        } ${
                                    variant === 'outline'
                                        ? 'bg-transparent border-b border-border dark:border-border-dark  focus:border-border focus:dark:border-border-dark '
                                        : 'bg-base-100  input-bordered rounded-2xl'
                                } ${
                                    !disabled
                                        ? 'opacity-80'
                                        : 'opacity-50 cursor-not-allowed'
                                } ${
                                    error ? 'input-error border-red-500' : ''
                                } ${
                                    textAlign === 'center'
                                        ? 'text-center'
                                        : textAlign === 'right'
                                        ? 'text-right'
                                        : 'text-start'
                                } ${
                                    sizeClasses[size] // Apply size-based classes
                                }`}
                                value={value ?? ''}
                                readOnly={readOnly}
                                onChange={(e) => {
                                    if (onChange) {
                                        onChange(e.target.value);
                                    }
                                }}
                                disabled={disabled}
                                required={required}
                                aria-invalid={error ? 'true' : 'false'}
                                {...(type === 'number'
                                    ? { pattern: '[0-9.]*' }
                                    : {})}
                            />
                        ) : mask ? (
                            <InputMask
                                mask={mask}
                                maskChar={maskChar}
                                value={value ?? ''}
                                onChange={(e: any) => {
                                    if (onChange) {
                                        onChange(e.target.value);
                                    }
                                }}
                                disabled={disabled}
                                required={required}
                                readOnly={readOnly}
                            >
                                {(inputProps) => (
                                    <input
                                        {...inputProps}
                                        ref={ref}
                                        type={inputType}
                                        name={name}
                                        autoFocus={autoFocus}
                                        placeholder={placeholder}
                                        className={commonInputClasses}
                                        aria-invalid={error ? 'true' : 'false'}
                                    />
                                )}
                            </InputMask>
                        ) : (
                            <input
                                min={0}
                                type={inputType}
                                name={name}
                                ref={ref}
                                placeholder={placeholder}
                                className={commonInputClasses}
                                readOnly={readOnly}
                                value={value ?? ''}
                                onChange={(e) => {
                                    if (onChange) {
                                        onChange(e.target.value);
                                    }
                                }}
                                disabled={disabled}
                                required={required}
                                aria-invalid={error ? 'true' : 'false'}
                                {...(type === 'number'
                                    ? { pattern: '[0-9.]*' }
                                    : {})}
                                autoFocus={autoFocus}
                            />
                        )}

                        {/* End Icon */}
                        {shouldShowDefaultEyeIcon && (
                            <button
                                type="button"
                                onClick={togglePasswordVisibility}
                                className={`absolute end-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors duration-200 ${
                                    disabled
                                        ? 'opacity-50 cursor-not-allowed'
                                        : ''
                                }`}
                                aria-label={
                                    showPassword
                                        ? 'Hide password'
                                        : 'Show password'
                                }
                                disabled={disabled}
                            >
                                {showPassword ? (
                                    <EyeSlashIcon className="w-5 h-5" />
                                ) : (
                                    <EyeIcon className="w-5 h-5" />
                                )}
                            </button>
                        )}

                        {/* If endIcon is provided and it's not the default eye icon */}
                        {endIcon && (
                            <span className="absolute end-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                {endIcon}
                            </span>
                        )}
                    </div>

                    {/* Helper Text or Error Message */}
                    {(helperText || error) && (
                        <div className="label">
                            {error ? (
                                <span className="label-text-alt text-red-500">
                                    {error}
                                </span>
                            ) : (
                                <span className="label-text-alt">
                                    {helperText}
                                </span>
                            )}
                        </div>
                    )}
                </label>
            </div>
        );
    }
);
